import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { getSpirit, SPIRITS, GOETIA_SOURCES, padNum, localizeSpirit } from "../data/goetia";
import { Sigil } from "../components/goetia/Sigil";
import { Reveal } from "../components/Reveal";
import { whatsappLink } from "../config/site";
import { useLang } from "../config/i18n";

const Section = ({ title, children, testid }) => (
  <div data-testid={testid} className="mb-10">
    <h3 className="text-[11px] font-semibold tracking-[0.25em] uppercase text-[#C5A059] mb-4 pb-3 border-b border-[#22222B]">
      {title}
    </h3>
    {children}
  </div>
);

export default function GoetiaSpiritPage() {
  const { slug } = useParams();
  const raw = getSpirit(slug);
  const { lang, t } = useLang();
  const g = t.goetia;
  const spirit = raw ? localizeSpirit(raw, lang) : null;

  useEffect(() => {
    if (spirit) {
      document.title = `${spirit.name} — ${lang === "en" ? "Spirit" : "Espírito"} ${padNum(spirit.n)} ${lang === "en" ? "of the Goetia" : "da Goétia"} | Mestre Jonas Barcellos`;
      const meta = document.querySelector('meta[name="description"]');
      meta?.setAttribute("content", `${spirit.name}, ${spirit.ranks.join(", ")} ${lang === "en" ? "of the Ars Goetia: traditional description, attributes, appearance, sigil and legions according to the classical sources." : "da Ars Goetia: descrição tradicional, atributos, aparência, sigilo e legiões segundo as fontes clássicas."}`);
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = "Mestre Jonas Barcellos | Alta Magia Goétia Luciferiana e Consultoria Espiritual";
    };
  }, [spirit, lang]);

  if (!spirit) return <Navigate to="/goetia" replace />;

  const idx = SPIRITS.findIndex((s) => s.slug === slug);
  const prev = SPIRITS[idx - 1];
  const next = SPIRITS[idx + 1];

  const waMsg = lang === "en"
    ? `Hello, Mestre Jonas. I would like spiritual guidance about ${spirit.name} (spirit ${padNum(spirit.n)} of the Goetia).`
    : `Olá, Mestre Jonas. Gostaria de orientação espiritual sobre ${spirit.name} (${padNum(spirit.n)} da Goétia).`;

  return (
    <div className="bg-[#0A0A0C] pt-[100px] pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <Link
            data-testid="spirit-back-link"
            to="/goetia"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#A3A3AF] hover:text-[#C5A059] transition-colors mb-12"
          >
            <ArrowLeft size={14} /> {g.backLink}
          </Link>

          <header className="mb-14">
            <p className="font-heading text-6xl sm:text-7xl gold-text font-light leading-none mb-4">{padNum(spirit.n)}</p>
            <h1 data-testid="spirit-name" className="font-heading text-4xl sm:text-5xl text-[#F7F7F8] tracking-wide uppercase mb-4">
              {spirit.name}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              {spirit.ranks.map((r) => (
                <span key={r} className="px-4 py-1.5 rounded-sm border border-[#C5A059]/50 text-[#C5A059] text-[10px] font-semibold tracking-[0.2em] uppercase">
                  {r}
                </span>
              ))}
              <span className="text-xs text-[#6E6E7D] font-light">{spirit.legions} {g.legionsSuffix}</span>
            </div>
            <p className="mt-4 text-xs text-[#6E6E7D] font-light">{g.altLabel} {spirit.alt}</p>
          </header>
        </Reveal>

        <div className="grid lg:grid-cols-[340px_1fr] gap-12 lg:gap-16 items-start">
          <Reveal className="lg:sticky lg:top-28">
            <div className="p-8 rounded-2xl bg-[#121216] border border-[#22222B] text-center">
              <Sigil spirit={spirit} size="lg" />
              <p className="mt-10 text-[10px] tracking-[0.15em] uppercase text-[#6E6E7D]">
                {g.sigilCaption}
              </p>
            </div>
            <a
              data-testid="spirit-whatsapp-cta"
              href={whatsappLink(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold w-full mt-5"
            >
              {g.consultAbout} {spirit.name}
            </a>
          </Reveal>

          <div>
            <Reveal>
              <Section title={g.profileSections.description} testid="spirit-description">
                <p className="text-[15px] sm:text-base text-[#D1D1DB] leading-relaxed font-light">{spirit.description}</p>
              </Section>
              <Section title={g.profileSections.appearance} testid="spirit-appearance">
                <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">{spirit.appearance}</p>
              </Section>
              <Section title={g.profileSections.attributes} testid="spirit-attributes">
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                  {spirit.attributes.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-sm sm:text-[15px] text-[#D1D1DB] font-light">
                      <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#C5A059] shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </Section>
              <Section title={g.profileSections.symbolism} testid="spirit-symbolism">
                <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">{spirit.symbolism}</p>
              </Section>
              <Section title={g.profileSections.notes} testid="spirit-notes">
                <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light">{spirit.notes}</p>
              </Section>
              <Section title={g.profileSections.sources} testid="spirit-sources">
                <ul className="space-y-3">
                  {GOETIA_SOURCES.map((src) => (
                    <li key={src} className="flex items-start gap-3 text-sm text-[#A3A3AF] font-light leading-relaxed">
                      <BookOpen size={15} className="text-[#C5A059] shrink-0 mt-1" strokeWidth={1.5} />
                      {src}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs text-[#6E6E7D] font-light leading-relaxed">
                  {g.sourcesNote}
                </p>
              </Section>
            </Reveal>

            <div className="flex items-stretch gap-4 mt-4">
              {prev ? (
                <Link
                  data-testid="spirit-prev-link"
                  to={`/goetia/${prev.slug}`}
                  className="flex-1 p-5 rounded-xl bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/50 transition-colors group"
                >
                  <span className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#6E6E7D] mb-1.5">
                    <ArrowLeft size={12} /> {g.prevLabel}
                  </span>
                  <span className="font-heading text-base text-[#F7F7F8] group-hover:text-[#C5A059] transition-colors uppercase">
                    {padNum(prev.n)} — {prev.name}
                  </span>
                </Link>
              ) : <div className="flex-1" />}
              {next ? (
                <Link
                  data-testid="spirit-next-link"
                  to={`/goetia/${next.slug}`}
                  className="flex-1 p-5 rounded-xl bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/50 transition-colors text-right group"
                >
                  <span className="flex items-center justify-end gap-2 text-[10px] tracking-[0.2em] uppercase text-[#6E6E7D] mb-1.5">
                    {g.nextLabel} <ArrowRight size={12} />
                  </span>
                  <span className="font-heading text-base text-[#F7F7F8] group-hover:text-[#C5A059] transition-colors uppercase">
                    {padNum(next.n)} — {next.name}
                  </span>
                </Link>
              ) : <div className="flex-1" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
